import axios, { AxiosPromise } from 'axios';
import {
  IExamCheckAnswerCardBody,
  IExamPaperParams,
  IExamPaperReq,
  IExamParams,
  IExamQuizReq,
  IGetExamCheck,
  IGetExamCheckAnswer,
  IGetExamCheckAnswerCard,
  IGetExamCheckStart,
  IGetExamCheckSubjectInfo,
  IGetExamInfo,
  IGetExamPapers,
  IGetExamPurposes,
  IGetExamQuizzes,
  IGetExams,
  IGetExamSemesters,
  IGetExamSubjects,
  IGetPrtItems,
  IGetPtCurriculums,
  IGetPtItems,
  IGetSpeakingExams,
  IGetTestSubjects,
  IGetTestType,
  IGetTestTypes,
  IGetTestTypesParams,
  IPostTestTypeReq,
  IPrtItem,
  IPtCurriculum,
  IPtItem,
  IPublishExamReq,
  ITestSubjectReq,
  ITestSubjectSeqReq,
} from '../models/testCenter.model';
import QueryString from 'qs';

const uri = '/mgmt/test-center';

export const getTestType = (
  examTypeCode: number,
): AxiosPromise<IGetTestType> => {
  return axios.get(`${uri}/exam-type/${examTypeCode}`);
};

export const getTestTypes = (params?: IGetTestTypesParams): AxiosPromise<IGetTestTypes> => {
  return axios.get(`${uri}/exam-type`, { params });
};

export const postTestType = (req: IPostTestTypeReq): AxiosPromise => {
  return axios.post(`${uri}/exam-type`, req);
};

export const putTestType = (examTypeCode: number, req: IPostTestTypeReq): AxiosPromise => {
  return axios.put(`${uri}/exam-type/${examTypeCode}`, req);
};

export const getTestSubjects = (): AxiosPromise<IGetTestSubjects> => {
  return axios.get(`${uri}/exam-type/subject`);
};

export const createTestSubject = (
  examTypeCode: number,
  data: ITestSubjectReq,
): AxiosPromise => {
  return axios.post(`${uri}/exam-type/${examTypeCode}/subject`, data);
};

export const updateTestSubjectSeq = (
  examTypeCode: number,
  data: ITestSubjectSeqReq,
): AxiosPromise => {
  return axios.put(`${uri}/exam-type/${examTypeCode}/subject/seq`, data);
};

export const getPrtItems = (): AxiosPromise<IGetPrtItems> => {
  return axios.get(`${uri}/prt`);
};

export const updatePrtItems = (data: IPrtItem[]): AxiosPromise => {
  return axios.post(`${uri}/prt`, data);
};

export const getPtItems = (examTypeCode: number): AxiosPromise<IGetPtItems> => {
  return axios.get(`${uri}/plat/${examTypeCode}`);
};

export const updatePtItems = (
  examTypeCode: number,
  data: IPtItem[],
): AxiosPromise => {
  return axios.post(`${uri}/plat/${examTypeCode}`, data);
};

export const getPtCurriculums = (
  examTypeCode: number,
): AxiosPromise<IGetPtCurriculums> => {
  return axios.get(`${uri}/plat-course/${examTypeCode}`);
};

export const updatePtCurriculums = (
  examTypeCode: number,
  data: IPtCurriculum[],
): AxiosPromise => {
  return axios.post(`${uri}/plat-course/${examTypeCode}`, data);
};

export const getExams = (params: IExamParams): AxiosPromise<IGetExams> => {
  const qsParams = QueryString.stringify(params);
  return axios.get(`${uri}/exam-management?${qsParams}`);
};

export const getExamInfo = (examCode: number): AxiosPromise<IGetExamInfo> => {
  return axios.get(`${uri}/exam-management/${examCode}`);
};

export const createExam = (req: FormData): AxiosPromise => {
  return axios.post(`${uri}/exam-management`, req);
};

export const updateExam = (examCode: number, req: FormData): AxiosPromise => {
  return axios.put(`${uri}/exam-management/${examCode}`, req);
};

export const publishExam = (
  examCode: number,
  data: IPublishExamReq,
): AxiosPromise => {
  return axios.put(`${uri}/exam-management/open/${examCode}`, data);
};

export const getExamSubjects = (
  examCode: number,
): AxiosPromise<IGetExamSubjects> => {
  return axios.get(`${uri}/exam-management/subject/${examCode}`);
};

export const getExamPapers = (
  params: IExamPaperParams,
): AxiosPromise<IGetExamPapers> => {
  const qsParams = QueryString.stringify(params);
  return axios.get(`${uri}/exam-management/exam-paper?${qsParams}`);
};

export const updateExamPaper = (
  examSubjectCode: number,
  data: IExamPaperReq,
): AxiosPromise => {
  return axios.put(`${uri}/exam-management/subject/${examSubjectCode}`, data);
};

export const deleteExamPaper = (examSubjectCode: number): AxiosPromise => {
  return axios.delete(`${uri}/exam-management/subject/${examSubjectCode}`);
};

export const getExamPurposes = (): AxiosPromise<IGetExamPurposes> => {
  return axios.get(`${uri}/exam-management/exam-paper/purpose`);
};

export const getExamQuizzes = (
  examCode: number,
  examSubjectCode: number,
): AxiosPromise<IGetExamQuizzes> => {
  return axios.get(
    `${uri}/exam-management/quiz/${examCode}/${examSubjectCode}`,
  );
};

export const updateExamQuiz = (
  examCode: number,
  examSubjectCode: number,
  data: IExamQuizReq[],
): AxiosPromise => {
  return axios.put(
    `${uri}/exam-management/quiz/${examCode}/${examSubjectCode}`,
    data,
  );
};

export const getSpeakingExams = (): AxiosPromise<IGetSpeakingExams> => {
  return axios.get(`${uri}/exam-management/speaking-exam`);
};

export const getExamCheck = (examCode: number): AxiosPromise<IGetExamCheck> =>
  axios.get(`${uri}/exam-management/exam-check/${examCode}`);

export const getExamCheckStart = (
  examCode: number,
): AxiosPromise<IGetExamCheckStart> =>
  axios.get(`${uri}/exam-management/exam-check/start/${examCode}`);

export const getExamCheckAnswer = (
  examCode: number,
  examTestCode: number,
): AxiosPromise<IGetExamCheckAnswer> =>
  axios.get(`${uri}/exam-management/exam-check/${examCode}/${examTestCode}`);

export const getExamCheckSubjectInfo = (
  examCode: number,
): AxiosPromise<IGetExamCheckSubjectInfo> =>
  axios.get(`${uri}/exam-management/exam-check/subject-info/${examCode}`);

export const getExamCheckAnswerCard = (
  examTestCode: number,
  examSubjectCode: number,
): AxiosPromise<IGetExamCheckAnswerCard> =>
  axios.get(
    `${uri}/exam-management/exam-check/answer-card/${examTestCode}/${examSubjectCode}`,
  );

export const postExamCheckAnswerCard = (
  examTestCode: number,
  examSubjectCode: number,
  body: IExamCheckAnswerCardBody[],
): AxiosPromise<IGetExamCheckAnswerCard> =>
  axios.post(
    `${uri}/exam-management/exam-check/answer-card/${examTestCode}/${examSubjectCode}`,
    body,
  );

export const getExamCheckReview = (
  examTestCode: number,
): AxiosPromise<IGetExamCheckStart> =>
  axios.get(`${uri}/exam-management/exam-check/review/${examTestCode}`);

export const postExamCheckReview = (examTestCode: number, checkType: string): AxiosPromise =>
  axios.put(`${uri}/exam-management/exam-check/review/${examTestCode}/${checkType}`);


export const getExamSemesters = (): AxiosPromise<IGetExamSemesters> =>
  axios.get(`/mgmt/semesters`);